/**
 * Step 4: Create fans and er_wheel for all tunnels
 *
 * Logic:
 * 1. Rate er_wheel (only for first tunnel); return error if rate doesn't return model
 * 2. Rate all fans in all tunnels; return error if any rate doesn't return model
 * 3. If all rates return model, create fans and er_wheel for first tunnel, then fans for remaining tunnels
 */
import { rateEnergyWheel } from '../../designSections/sections';
import { createEnergyWheelComponent } from '../../designComponents/createEnergyWheel';
import {
  IBotData,
  IAHUParams,
  ISection,
  DEFAULT_COEFFICIENTS,
  IErWheelParams,
} from '../../../../models';
import { SectionCreationContext } from '../../../../models/bot';
import { dispatchMutation } from '../../../../utils/dispatch.utils';
import { ahuApi } from '../../../../services';
import { getFanAndNextSection, rateFan } from '../../designComponents/fan';
import { createFanForTunnel } from '../../designComponents/createFan';

export async function step4CreateFansAndRotaries(
  botData: IBotData,
  tunnels: any[],
  tunnelIds: number[],
  casingInsulationId: number | null,
  casingInnerlinerId: number | null,
  floorInnerlinerId: number | null,
  ahuParams: IAHUParams,
  rotaryParams: IErWheelParams,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
  checkStopped?: () => boolean,
): Promise<{
  updatedTunnels: any[];
  rateErrors: Array<{ sectionId: string; sectionType: string; error: string }>;
}> {
  const rateErrors: Array<{
    sectionId: string;
    sectionType: string;
    error: string;
  }> = [];
  let updatedTunnels = [...tunnels];
  // ========================================================================
  // PHASE 1: Rate er_wheel (only for first tunnel)
  // ========================================================================
  if (botData.tunnels.length > 0) {
    const erWheelSection = botData.tunnels[0].sections.find(
      (s: any) => s.type === 'er_wheel',
    );
    if (erWheelSection) {
      if (checkStopped?.()) {
        return {
          updatedTunnels,
          rateErrors,
        };
      }
      const erWheelRating = await rateEnergyWheel(
        erWheelSection,
        botData,
        rotaryParams,
        getOptions,
        onProgress,
      );
      if (erWheelRating.error || !erWheelRating.model) {
        rateErrors.push({
          sectionId: erWheelSection.id,
          sectionType: 'er_wheel',
          error: 'No suitable rotary heat-exchanger available',
        });
        return {
          updatedTunnels,
          rateErrors,
        };
      }
      erWheelSection._ratingData = erWheelRating;
    }
  }
  // ========================================================================
  // PHASE 2: Rate all fans in all tunnels
  // ========================================================================
  for (
    let tunnelIndex = 0;
    tunnelIndex < botData.tunnels.length;
    tunnelIndex++
  ) {
    if (checkStopped?.()) {
      return {
        updatedTunnels,
        rateErrors,
      };
    }
    const botTunnel = botData.tunnels[tunnelIndex];
    const validSections = botTunnel.sections.filter(
      (s: any) => s.type && s.type.trim() !== '',
    );
    const { fan } = getFanAndNextSection(validSections);
    if (!fan) {
      continue;
    }
    const fanRatingData = await rateFan(fan, botData, ahuParams, getOptions);
    onProgress?.(DEFAULT_COEFFICIENTS.fan_rate);
    if (fanRatingData.error || !fanRatingData.model) {
      rateErrors.push({
        sectionId: fan.id,
        sectionType: 'fan',
        error: 'No suitable fan available',
      });
      return {
        updatedTunnels,
        rateErrors,
      };
    }
    fan._ratingData = fanRatingData;
  }
  // ========================================================================
  // PHASE 3: Create fans and er_wheel for first tunnel, then fans for remaining tunnels
  // ========================================================================
  let firstTunnelErWheelRating: any = null;
  let first_section_id = 0;
  if (botData.tunnels.length > 0) {
    const firstSections = botData.tunnels[0].sections.filter(
      (s: any) => s.type && s.type.trim() !== '',
    );
    const firstErWheel = firstSections.find((s: any) => s.type === 'er_wheel');
    if (firstErWheel && firstErWheel._ratingData) {
      firstTunnelErWheelRating = firstErWheel._ratingData;
    }
  }
  for (
    let tunnelIndex = 0;
    tunnelIndex < botData.tunnels.length;
    tunnelIndex++
  ) {
    if (checkStopped?.()) {
      break;
    }
    const botTunnel = botData.tunnels[tunnelIndex];
    const currentTunnelId = tunnelIds[tunnelIndex];
    const validSections = botTunnel.sections.filter(
      (s) => s.type && s.type.trim() !== '',
    );
    const sections = validSections.filter(
      (s) => s.type === 'fan' || s.type === 'er_wheel',
    );

    for (const s of sections) {
      if (s.type === 'fan') {
        // Create fan for this tunnel (with pre-rated data from phase 2)
        const fanResult = await createFanForTunnel(
          botTunnel,
          currentTunnelId,
          validSections,
          botData,
          casingInsulationId,
          casingInnerlinerId,
          floorInnerlinerId,
          getOptions,
          onProgress,
        );
        if (fanResult.fanError) {
          rateErrors.push(fanResult.fanError);
          return {
            updatedTunnels,
            rateErrors,
          };
        }
        s.component = fanResult.tunnelFan;
      } else if (s.type === 'er_wheel') {
        // Create er_wheel section for first tunnel; create second tunnel er_wheel section + component when tunnelIndex === 1
        if (tunnelIndex === 0) {
          const erWheelSection = s;
          if (erWheelSection && erWheelSection._ratingData) {
            const erWheelSectionData: ISection = await dispatchMutation(
              ahuApi.endpoints.createSection,
              {
                body: {
                  length: 12,
                  sitting_on_id: null,
                  split: false,
                  tag: '',
                  tunnel_id: currentTunnelId,
                  casing_insulation_id: casingInsulationId,
                  casing_innerliner_id: casingInnerlinerId,
                  floor_innerliner_id: floorInnerlinerId,
                },
              },
              getOptions(),
            );
            onProgress?.(DEFAULT_COEFFICIENTS.create_section);
            erWheelSection.model = erWheelSectionData;
            first_section_id = erWheelSectionData.id;
          }
        } else if (tunnelIndex === 1 && firstTunnelErWheelRating) {
          const erWheelSection = validSections.find(
            (s: any) => s.type === 'er_wheel',
          );
          if (erWheelSection) {
            const secondErWheelSectionData: ISection = await dispatchMutation(
              ahuApi.endpoints.createSection,
              {
                body: {
                  length: 12,
                  sitting_on_id: first_section_id,
                  split: false,
                  tag: '',
                  tunnel_id: currentTunnelId,
                  casing_insulation_id: casingInsulationId,
                  casing_innerliner_id: casingInnerlinerId,
                  floor_innerliner_id: floorInnerlinerId,
                },
              },
              getOptions(),
            );
            onProgress?.(DEFAULT_COEFFICIENTS.create_section);
            erWheelSection.model = secondErWheelSectionData;
            const sectionContext: SectionCreationContext = {
              tunnelId: currentTunnelId,
              sizerSettings: null,
              thickness: null,
              casingInsulationId,
              casingInnerlinerId,
              floorInnerlinerId,
              validSectionsLength: validSections.length,
              getOptions,
              onProgress,
              checkStopped: checkStopped ?? (() => false),
            };
            await createEnergyWheelComponent(
              erWheelSection,
              first_section_id,
              secondErWheelSectionData.id,
              sectionContext,
              {
                model: firstTunnelErWheelRating.model,
                input: firstTunnelErWheelRating.input,
              },
            );
          }
        }
      }
    }
  }
  return {
    updatedTunnels,
    rateErrors,
  };
}
